import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ProgressStepper from './components/ProgressStepper';
import Step0 from './components/steps/Step0.jsx';
import Step1 from './components/steps/Step1.jsx';
import Step2 from './components/steps/Step2.jsx';
import Step2Body from './components/steps/Step2Body.jsx';
import Step2Breast from './components/steps/Step2Breast.jsx';
import Step2Ass from './components/steps/Step2Ass.jsx';
import Step3 from './components/steps/Step3.jsx';
// import Step3Hair from './components/steps/Step3Hair.jsx';
import Step4 from './components/steps/Step4.jsx';
import Step4_5 from './components/steps/Step4_5.jsx';
import Step5 from './components/steps/Step5.jsx';

import { QUIZ_STEPS, STORAGE_KEYS, QUIZ_OPTIONS_DATA, getQuizOptionsData } from './constants';

import { useQuizSelections } from './hooks/useLocalStorage';
import { preloadNextStepImages, preloadVideos } from './utils/imagePreloader';
import { initTracking } from './utils/tracking';
import { useDesign } from './contexts/DesignContext';
import { useSteps } from './hooks/useSteps';
import CountdownTimer from './components/shared/CountdownTimer';
import AnimatedTape from './components/shared/AnimatedTape';
import LogoSvg from './assets/Logo.svg';

const STEPS_COMPONENTS = {
  welcome: Step0,
  step1: Step1,
  step2: Step2,
  step2_body: Step2Body,
  step2_breast: Step2Breast,
  step2_ass: Step2Ass,
  step3: Step3,
  // step3_hair: Step3Hair,
  step4: Step4,
  step4_5: Step4_5,
  step5: Step5,
};

const validateStepData = (step, answer) => {
  switch (step) {
    case 'welcome':
      return answer && answer.trim() !== '';

    case 'step1':
      return answer && answer.ethnicity && answer.age;

    case 'step2':
      return answer && answer.bodyType && answer.breastSize && answer.buttSize;

    case 'step2_body':
      return answer && answer.bodyType;

    case 'step2_breast':
      return answer && answer.breastSize;

    case 'step2_ass':
      return answer && answer.buttSize;

    case 'step3':
      return answer && answer.hairColor && answer.hairStyle && answer.eyeColor;

    // case 'step3_hair':
    //   return answer && answer.hairColor && answer.hairStyle && answer.eyeColor;

    case 'step4':
      return answer && answer.voice && answer.voiceAudio && answer.personality;

    case 'step4_5':
      return answer && answer.kink;

    case 'step5':
      return answer && answer.trim() !== '';

    default:
      return true;
  }
};

function Home() {
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedStep, setDisplayedStep] = useState(0);
  const [transitionState, setTransitionState] = useState('idle'); // idle | fade-out | fade-in

  const location = useLocation();
  const navigate = useNavigate();
  const [selections, setSelections] = useQuizSelections(location.pathname);
  const pendingStepRef = useRef(null);

  const { t } = useTranslation();
  const { isNewDesign } = useDesign();
  const { getActiveSteps } = useSteps();

  const activeSteps = useMemo(() => {
    return getActiveSteps(QUIZ_STEPS);
  }, [getActiveSteps]);

  const hiddenSteps = useMemo(() => {
    const loadingIndex = activeSteps.indexOf('loading');
    const createAccountIndex = activeSteps.indexOf('createAccount');
    const loginIndex = activeSteps.indexOf('login');

    return [loadingIndex, createAccountIndex, loginIndex].filter(
      (index) => index !== -1,
    );
  }, [activeSteps]);

  useEffect(() => {
    if (activeSteps[currentStep] === 'step5') {
      navigate('summary', { replace: true });
    }
  }, [currentStep, activeSteps, navigate]);

  // Proactively preload videos for the next step while the user interacts with the current one.
  // This gives the browser the user's full interaction time to buffer videos before they appear.
  useEffect(() => {
    const STEP_TO_VIDEO_DATA = {
      welcome: 'ethnicity',
      step1: 'bodyType',
      step2_body: 'breastSize',
      step2_breast: 'buttSize',
    };

    const currentStepName = activeSteps[displayedStep];
    const nextDataKey = STEP_TO_VIDEO_DATA[currentStepName];
    if (!nextDataKey) return;

    const selectedStyle = selections?.welcome || 'Realistic';
    const nextStepItems = getQuizOptionsData(nextDataKey, selectedStyle);
    const videoUrls = nextStepItems.map((item) => item.video).filter(Boolean);

    if (videoUrls.length > 0) {
      preloadVideos(videoUrls);
    }
  }, [displayedStep, activeSteps, selections?.welcome]);

  useEffect(() => {
    if (isNewDesign) {
      document.body.classList.add('ndg-mode');
    } else {
      document.body.classList.remove('ndg-mode');
    }

    return () => {
      document.body.classList.remove('ndg-mode');
    };
  }, [isNewDesign]);

  const transitionToStep = useCallback(
    (newStep) => {
      if (transitionState !== 'idle') return;

      pendingStepRef.current = newStep;
      setTransitionState('fade-out');
    },
    [transitionState],
  );

  const handleAnswer = useCallback(
    (step, answer) => {
      if (!validateStepData(step, answer)) return;

      if (step === 'welcome' || step.startsWith('step')) {
        const existing = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}',
        );

        const updated = {
          ...existing,
          [step]: answer,
        };

        localStorage.setItem(
          STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS,
          JSON.stringify(updated),
        );

        // Используем полный updated (включая welcomeVideoSrc/welcomePosterSrc из Step0),
        // чтобы хук не перезатирал поля которые были записаны напрямую в localStorage
        setSelections(updated);
      } else {
        setSelections((prev) => ({
          ...prev,
          [step]: answer,
        }));
      }

      setTimeout(() => {
        preloadNextStepImages(step, QUIZ_OPTIONS_DATA);
      }, 100);

      transitionToStep(currentStep + 1);
    },
    [setSelections, currentStep, transitionToStep],
  );

  const handlePrevious = useCallback(() => {
    if (transitionState !== 'idle' || currentStep === 0) return;

    transitionToStep(currentStep - 1);
  }, [transitionState, currentStep, transitionToStep]);

  const resetQuiz = useCallback(() => {
    setCurrentStep(0);
    setDisplayedStep(0);
    setSelections({});
    setTransitionState('idle');

    localStorage.removeItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS);
  }, [setSelections]);

  useEffect(() => {
    if (transitionState === 'fade-out') {
      const timer = setTimeout(() => {
        setCurrentStep(pendingStepRef.current);
        setDisplayedStep(pendingStepRef.current);
        setTransitionState('fade-in');
      }, 300);

      return () => clearTimeout(timer);
    }

    if (transitionState === 'fade-in') {
      const timer = setTimeout(() => {
        setTransitionState('idle');
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [transitionState]);

  useEffect(() => {
    initTracking();
  }, []);

  // Update body data-attribute for current step (for Valentine's theme)
  useEffect(() => {
    const stepName = activeSteps[currentStep];
    document.body.setAttribute('data-current-step', stepName);

    return () => {
      document.body.removeAttribute('data-current-step');
    };
  }, [currentStep]);

  const getVisibleSteps = useCallback(
    () => activeSteps.filter((_, i) => !hiddenSteps.includes(i)),
    [activeSteps, hiddenSteps],
  );

  const shouldShowProgressStepper = useCallback(
    () => !hiddenSteps.includes(currentStep),
    [currentStep, hiddenSteps],
  );

  const currentStepName = activeSteps[displayedStep];
  const CurrentStepComponent = STEPS_COMPONENTS[currentStepName];
  const isTransitioning = transitionState !== 'idle';

  if (!CurrentStepComponent) {
    return (
      <div className="quizContentWrapper">
        <div className="quizContent">
          <h1>{t('step_not_found')}</h1>

          <p>
            <strong>URL:</strong> {location.pathname}
          </p>
          <p>
            <strong>Current step:</strong> {currentStep}
          </p>
          <p>
            <strong>Displayed step:</strong> {displayedStep}
          </p>
          <p>
            <strong>Step name:</strong> {currentStepName}
          </p>
          <p>
            <strong>Is new design:</strong> {isNewDesign ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Active steps:</strong> {activeSteps.join(', ')}
          </p>

          <button onClick={resetQuiz}>{t('reset_quiz')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`app ${isTransitioning ? 'no-click' : ''}`}>
      <img src={LogoSvg} alt="Swipey" className="app-logo" width={100} height={24} />
      {currentStepName === 'step5' && <CountdownTimer />}
      <AnimatedTape />
      {shouldShowProgressStepper() && (
        <ProgressStepper
          currentStep={currentStep}
          totalSteps={getVisibleSteps().length}
        />
      )}

      <div
        className={`step-wrapper ${
          transitionState === 'fade-out'
            ? 'fade-out'
            : transitionState === 'fade-in'
            ? 'fade-in'
            : ''
        }`}
      >
        <CurrentStepComponent
          onAnswer={handleAnswer}
          selections={selections}
          onPrevious={handlePrevious}
          isTransitioning={isTransitioning}
        />
      </div>
    </div>
  );
}

export default Home;
